import React, { useState } from "react";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import mail from "../assets/images/navbar/mail.png";
import scooter from "../assets/images/navbar/scooter.png";
import volume from "../assets/images/navbar/volume.png";
import whatsapp from "../assets/images/navbar/whatsapp.png";

type ManageCommunicationProps = {
  setIsManageCommunicationOpen: (open: boolean) => void;
};

const STORAGE_KEY = "kitchenCommunicationPrefs";

const defaultPrefs = {
  notifications: {
    whatsapp: true,
    orderReminders: true,
    ringVolume: true,
    riderNotifications: true,
  },
  businessReports: {
    whatsapp: true,
    email: false,
  },
  weeklyReports: {
    whatsapp: true,
    email: true,
  },
};

const ManageCommunication = ({
  setIsManageCommunicationOpen,
}: ManageCommunicationProps) => {
  const saved = (() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    } catch {
      return null;
    }
  })();

  const [notifications, setNotifications] = useState(
    saved?.notifications || defaultPrefs.notifications
  );
  const [businessReports, setBusinessReports] = useState(
    saved?.businessReports || defaultPrefs.businessReports
  );
  const [weeklyReports, setWeeklyReports] = useState(
    saved?.weeklyReports || defaultPrefs.weeklyReports
  );

  React.useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ notifications, businessReports, weeklyReports })
    );
  }, [notifications, businessReports, weeklyReports]);

  // Toggle Switch Component
  const ToggleSwitch = ({
    enabled,
    onChange,
  }: {
    enabled: boolean;
    onChange: () => void;
  }) => (
    <div
      onClick={onChange}
      className={`relative inline-flex h-[24px] w-[44px] items-center rounded-full cursor-pointer transition-colors duration-200 ${
        enabled ? "bg-[#22C55E]" : "bg-[#E5E7EB]"
      }`}
    >
      <span
        className={`inline-block h-[20px] w-[20px] transform rounded-full bg-white transition-transform duration-200 ${
          enabled ? "translate-x-[22px]" : "translate-x-[2px]"
        }`}
      />
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300 overflow-y-auto"
      onClick={() => setIsManageCommunicationOpen(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-900 transform duration-300 ease-in-out rounded-l-[20px] shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] overflow-y-auto transition-colors"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full inline-flex flex-col items-start rounded-l-[24px] bg-white dark:bg-gray-900 p-6 transition-colors">
          {/* Header */}
          <div className="w-full flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <img
                src={arrowLeft}
                alt="back"
                className="flex w-[36px] h-[36px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] dark:border-gray-600 bg-[rgba(255,255,255,0.25)] dark:bg-gray-800 cursor-pointer transition-colors dark:invert"
                onClick={() => setIsManageCommunicationOpen(false)}
              />
              <h1 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[20px] font-semibold leading-[28px] transition-colors">
                Manage Communications
              </h1>
            </div>
            <img
              src={cancel}
              alt="close"
              className="w-[36px] h-[36px] p-[7.5px] rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] dark:border-gray-600 bg-[rgba(255,255,255,0.25)] dark:bg-gray-800 cursor-pointer transition-colors dark:invert"
              onClick={() => setIsManageCommunicationOpen(false)}
            />
          </div>

          {/* Content Sections */}
          <div className="w-full space-y-6">
            {/* Notifications Section */}
            <div className="border dark:border-gray-700 rounded-[16px] p-4 bg-white dark:bg-gray-800 transition-colors">
              <h2 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[16px] font-semibold leading-[22px] mb-4 border-b dark:border-gray-700 pb-3 transition-colors">
                Notifications
              </h2>
              <div className="space-y-4">
                {/* WhatsApp Notifications */}
                <div className="flex justify-between items-center border-b dark:border-gray-700 pb-3 transition-colors">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                        fill="#25D366"
                      />
                    </svg>
                    <span className="text-[#040404] dark:text-gray-200 font-Urbanist text-[14px] font-normal leading-[20px] transition-colors">
                      WhatsApp Notifications
                    </span>
                  </div>
                  <ToggleSwitch
                    enabled={notifications.whatsapp}
                    onChange={() =>
                      setNotifications({
                        ...notifications,
                        whatsapp: !notifications.whatsapp,
                      })
                    }
                  />
                </div>

                {/* Order Reminders */}
                <div className="flex justify-between items-center border-b dark:border-gray-700 pb-3 transition-colors">
                  <div className="flex items-center gap-3">
                    <img
                      src={scooter}
                      alt="Scooter"
                      className="w-[20px] h-[20px] dark:invert"
                    />

                    <span className="text-[#040404] dark:text-gray-200 font-Urbanist text-[14px] font-normal leading-[20px] transition-colors">
                      Order Reminders
                    </span>
                  </div>
                  <ToggleSwitch
                    enabled={notifications.orderReminders}
                    onChange={() =>
                      setNotifications({
                        ...notifications,
                        orderReminders: !notifications.orderReminders,
                      })
                    }
                  />
                </div>

                {/* Ring Volume */}
                <div className="flex justify-between items-center border-b dark:border-gray-700 pb-3 transition-colors">
                  <div className="flex items-center gap-3">
                    <img
                      src={volume}
                      alt="volume"
                      className="w-[20px] h-[20px] dark:invert"
                    />
                    <span className="text-[#040404] dark:text-gray-200 font-Urbanist text-[14px] font-normal leading-[20px] transition-colors">
                      Ring Volume
                    </span>
                  </div>
                  <ToggleSwitch
                    enabled={notifications.ringVolume}
                    onChange={() =>
                      setNotifications({
                        ...notifications,
                        ringVolume: !notifications.ringVolume,
                      })
                    }
                  />
                </div>

                {/* Rider Notifications */}
                <div className="flex justify-between items-center border-b dark:border-gray-700 pb-3 transition-colors">
                  <div className="flex items-center gap-3">
                    <img
                      src={scooter}
                      alt="scooter"
                      className="w-[20px] h-[20px] dark:invert"
                    />
                    <span className="text-[#040404] dark:text-gray-200 font-Urbanist text-[14px] font-normal leading-[20px] transition-colors">
                      Rider Notifications
                    </span>
                  </div>
                  <ToggleSwitch
                    enabled={notifications.riderNotifications}
                    onChange={() =>
                      setNotifications({
                        ...notifications,
                        riderNotifications: !notifications.riderNotifications,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Business Reports Section */}
            <div className="border dark:border-gray-700 rounded-[16px] p-4 bg-white dark:bg-gray-800 transition-colors">
              <h2 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[16px] font-semibold leading-[22px] mb-4 border-b dark:border-gray-700 pb-3 transition-colors">
                Business Reports
              </h2>
              <div className="space-y-4">
                {/* Share on Whatsapp */}
                <div className="flex justify-between items-center border-b dark:border-gray-700 pb-3 transition-colors">
                  <div className="flex items-center gap-3 ">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                        fill="#25D366"
                      />
                    </svg>
                    <span className="text-[#040404] dark:text-gray-200 font-Urbanist text-[14px] font-normal leading-[20px] transition-colors">
                      Share on Whatsapp
                    </span>
                  </div>
                  <ToggleSwitch
                    enabled={businessReports.whatsapp}
                    onChange={() =>
                      setBusinessReports({
                        ...businessReports,
                        whatsapp: !businessReports.whatsapp,
                      })
                    }
                  />
                </div>

                {/* Share on E-mail */}
                <div className="flex justify-between items-center border-b dark:border-gray-700 pb-3 transition-colors">
                  <div className="flex items-center gap-3">
                    <img src={mail} alt="email" className="w-[20px] h-[20px] dark:invert" />
                    <span className="text-[#040404] dark:text-gray-200 font-Urbanist text-[14px] font-normal leading-[20px] transition-colors">
                      Share on E-mail
                    </span>
                  </div>
                  <ToggleSwitch
                    enabled={businessReports.email}
                    onChange={() =>
                      setBusinessReports({
                        ...businessReports,
                        email: !businessReports.email,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Weekly Reports Section */}
            <div className="border dark:border-gray-700 rounded-[16px] p-4 bg-white dark:bg-gray-800 transition-colors">
              <h2 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[16px] font-semibold leading-[22px] mb-4 border-b dark:border-gray-700 pb-3 transition-colors">
                Weekly Reports
              </h2>
              <div className="space-y-4">
                {/* Share on Whatsapp */}
                <div className="flex justify-between items-center border-b dark:border-gray-700 pb-3 transition-colors">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                        fill="#25D366"
                      />
                    </svg>
                    <span className="text-[#040404] dark:text-gray-200 font-Urbanist text-[14px] font-normal leading-[20px] transition-colors">
                      Share on Whatsapp
                    </span>
                  </div>
                  <ToggleSwitch
                    enabled={weeklyReports.whatsapp}
                    onChange={() =>
                      setWeeklyReports({
                        ...weeklyReports,
                        whatsapp: !weeklyReports.whatsapp,
                      })
                    }
                  />
                </div>

                {/* Share on E-mail */}
                <div className="flex justify-between items-center border-b dark:border-gray-700 pb-3 transition-colors">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 dark:invert" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22 6L12 13L2 6"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="text-[#040404] dark:text-gray-200 font-Urbanist text-[14px] font-normal leading-[20px] transition-colors">
                      Share on E-mail
                    </span>
                  </div>
                  <ToggleSwitch
                    enabled={weeklyReports.email}
                    onChange={() =>
                      setWeeklyReports({
                        ...weeklyReports,
                        email: !weeklyReports.email,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCommunication;
